import { Controller } from 'stimulus';

// The scan station page. A keyboard-wedge scanner types the barcode into the
// scan box and presses Enter; a person can type a job number instead. Every
// scan is POSTed to the station, which answers with an HTML card (and a log
// row when something was recorded). The scan box keeps focus between scans so
// a shared tablet or PC never needs a click. Nothing changes on a job until
// "Receive job" is pressed or the same traveler is scanned a second time.
//
// Cards clear themselves so a shared screen goes back to the scan box: an
// answer card after a minute (the log row keeps the record), and a pending
// card after two minutes with nothing decided, so a scan somebody walked away
// from is never confirmed by the next person's Enter. Typing in the card
// restarts the clock. The timeouts come from data attributes on the page.
export default class extends Controller {
  static targets = ['input', 'result', 'rows', 'count', 'empty', 'note', 'operator'];

  connect() {
    this.pendingJobId = null;
    this.pendingForce = false;
    this.lastCode = '';
    this.clearTimer = null;
    this.timerOutcome = null;
    this.refocus = this.refocus.bind(this);
    this.touch = this.touch.bind(this);
    document.addEventListener('click', this.refocus);
    ['input', 'change', 'focusin'].forEach((name) => this.resultTarget.addEventListener(name, this.touch));
    this.focusInput();
  }

  disconnect() {
    document.removeEventListener('click', this.refocus);
    ['input', 'change', 'focusin'].forEach((name) => this.resultTarget.removeEventListener(name, this.touch));
    this.stopTimer();
  }

  // Clicking anywhere that is not a control puts the cursor back in the scan box.
  refocus(event) {
    if (event.target.closest('input, textarea, select, button, a, label, .ts-wrapper, .ts-dropdown')) { return; }
    this.focusInput();
  }

  focusInput() {
    this.inputTarget.focus();
  }

  keydown(event) {
    if (event.key !== 'Enter') { return; }
    event.preventDefault();

    const code = this.inputTarget.value.trim();
    this.inputTarget.value = '';

    // A second scan of the traveler on the pending card (or a bare Enter) confirms it.
    if (this.pendingJobId && (code === '' || this.jobIdFrom(code) === this.pendingJobId)) {
      this.receive();
      return;
    }
    if (code === '') { return; }

    this.lookup(code, false);
  }

  // Mirrors ScanStations::Lookup.job_id_from so a repeat scan can be recognised without a round trip.
  jobIdFrom(code) {
    const fromUrl = code.match(/\/jobs\/(\d+)(?:\D|$)/);
    const fromNumber = code.match(/^\s*(?:job\s*#?\s*)?(\d+)\s*$/i);
    const match = fromUrl || fromNumber;
    return match ? parseInt(match[1], 10) : null;
  }

  lookup(code, force) {
    this.post(this.data.get('lookupUrl'), { code, force: force ? '1' : '' }).then((json) => {
      this.lastCode = code;
      this.render(json);
      if (json.outcome === 'pending') {
        this.pendingJobId = json.job_id;
        this.pendingForce = force;
      } else {
        this.pendingJobId = null;
        this.pendingForce = false;
      }
      this.focusInput();
    });
  }

  receive() {
    if (!this.pendingJobId) { return; }
    const jobId = this.pendingJobId;
    const note = this.hasNoteTarget ? this.noteTarget.value : '';
    const operatorId = this.hasOperatorTarget ? this.operatorTarget.value : '';
    const force = this.pendingForce ? '1' : '';
    this.pendingJobId = null;
    this.pendingForce = false;

    this.post(this.data.get('receiveUrl'), {
      job_id: jobId, note, operator_id: operatorId, code: this.lastCode, force,
    }).then((json) => {
      this.render(json);
      if (json.outcome === 'received') { this.beep(); }
      this.focusInput();
    });
  }

  // "Move to <status> anyway" on a completed job: look it up again with force so it becomes pending.
  override(event) {
    this.lookup(event.currentTarget.dataset.code, true);
  }

  cancel() {
    this.stopTimer();
    this.resultTarget.innerHTML = '';
    this.pendingJobId = null;
    this.pendingForce = false;
    this.focusInput();
  }

  startTimer(outcome) {
    this.stopTimer();
    const ms = parseInt(this.data.get(outcome === 'pending' ? 'pendingTimeout' : 'answerTimeout'), 10);
    if (!(ms > 0)) { return; }
    this.timerOutcome = outcome;
    this.clearTimer = setTimeout(() => this.expire(outcome), ms);
  }

  stopTimer() {
    if (this.clearTimer) { clearTimeout(this.clearTimer); }
    this.clearTimer = null;
  }

  // Someone is working in the card (typing a note, picking who scanned): restart the clock.
  touch() {
    if (this.clearTimer) { this.startTimer(this.timerOutcome); }
  }

  expire(outcome) {
    this.clearTimer = null;
    const card = this.resultTarget.firstElementChild;
    if (!card) { return; }
    const wasPending = outcome === 'pending';
    if (wasPending) {
      this.pendingJobId = null;
      this.pendingForce = false;
    }
    card.style.transition = 'opacity 0.5s';
    card.style.opacity = '0';
    setTimeout(() => {
      // A receive may have answered while the card was fading; leave the new card alone.
      if (card.parentNode !== this.resultTarget) { return; }
      this.resultTarget.innerHTML = wasPending ? this.clearedNotice() : '';
      if (wasPending) { this.startTimer('cleared'); }
    }, 500);
  }

  clearedNotice() {
    return '<div class="px-1 text-sm leading-5 text-gray-500" data-target="scan-station.cleared">'
      + 'Nothing was decided, so the scan was cleared. Scan the traveler again to receive it.</div>';
  }

  render(json) {
    this.resultTarget.innerHTML = json.card || '';
    this.startTimer(json.outcome);
    if (json.row) {
      this.rowsTarget.insertAdjacentHTML('afterbegin', json.row);
      if (this.hasEmptyTarget) { this.emptyTarget.classList.add('hidden'); }
    }
    if (json.outcome === 'received') {
      this.countTarget.textContent = String(parseInt(this.countTarget.textContent, 10) + 1);
    }
  }

  post(url, data) {
    return fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-CSRF-Token': Rails.csrfToken(),
      },
      body: JSON.stringify(data),
    }).then((response) => {
      // A signed-out session redirects to the login page: reload so the person sees it.
      if (response.redirected || !response.ok) {
        window.location.reload();
        throw new Error('Scan station request failed');
      }
      return response.json();
    });
  }

  // A short confirmation tone, since the person is usually looking at the samples, not the screen.
  beep() {
    try {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      const context = new AudioContext();
      const oscillator = context.createOscillator();
      const gain = context.createGain();
      oscillator.frequency.value = 880;
      gain.gain.value = 0.08;
      oscillator.connect(gain);
      gain.connect(context.destination);
      oscillator.start();
      oscillator.stop(context.currentTime + 0.12);
    } catch (error) {
      // No audio on this device; the green card is enough.
    }
  }
}
